/// <amd-module name="Repos/PullRequestList/Components/PullRequestSection" />
import * as React from "react";
import { PullRequestStatus } from "Repos/Common/Contracts/Git";
import {
    commentsColumn,
    creatorColumn,
    PullRequestTable,
    reviewersColumn,
    titleColumn,
    titleNoRepoColumn,
    updatesColumn
} from "Repos/PullRequestList/Components/PullRequestTable";
import { IPullRequest, IPullRequestSection, PullRequestVote } from "Repos/PullRequestList/Contracts";
import * as Resources from "Repos/PullRequestList/Resources";
import { ObservableLike, useObservable } from "VSS/Core/Observable";
import { ScreenBreakpoints, ScreenSize } from "VSS/Core/Util/Screen";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { useComponentContext } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { IVssPageService } from "VSS/Platform/Page";
import { tryGetLocalStorageValue, trySetLocalStorageValue } from "VSS/Platform/Util/Storage";
import { Button } from "VSSUI/Button";
import { CardContent, CustomCard } from "VSSUI/Card";
import { Header } from "VSSUI/Header";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { IconSize } from "VSSUI/Icon";
import { Observer } from "VSSUI/Observer";
import { Pill } from "VSSUI/Pill";
import { ColumnMore, ITableBreakpoint, ITableRow } from "VSSUI/Table";
import { IVssContributedTab, TabBar, TabProvider } from "VSSUI/Tabs";
import { css } from "VSSUI/Util";
import { ScreenSizeObserver } from "VSSUI/Utilities/ScreenSize";
import { ZeroData } from "VSSUI/ZeroData";

export interface IPullRequestSectionProps {
    forceCompactColumns?: boolean;
    onActivateRow?: (event: React.SyntheticEvent<HTMLElement>, tableRow: ITableRow<IPullRequest>) => void;
    onCustomizeUserView?: (onDismiss: () => void) => void;
    onToggleDecline: (pullRequest: IPullRequest) => void;
    onToggleFlagged: (pullRequest: IPullRequest) => void;
    showRepository?: boolean;
    section: IPullRequestSection;
}

const tableBreakpoints: ITableBreakpoint[] = [
    {
        breakpoint: ScreenBreakpoints.small,
        columnWidths: [0, -100, 0, 0, 0]
    },
    {
        breakpoint: ScreenBreakpoints.small,
        columnWidths: [ObservableLike.getValue(creatorColumn.width), -100, 0, ObservableLike.getValue(commentsColumn.width), 190]
    },
    {
        breakpoint: ScreenBreakpoints.medium,
        columnWidths: [
            ObservableLike.getValue(creatorColumn.width),
            ObservableLike.getValue(titleColumn.width),
            ObservableLike.getValue(reviewersColumn.width),
            ObservableLike.getValue(commentsColumn.width),
            ObservableLike.getValue(updatesColumn.width)
        ]
    }
];

export const PullRequestSection = (props: IPullRequestSectionProps) => {
    const { onActivateRow, onCustomizeUserView, onToggleDecline, onToggleFlagged, section, showRepository } = props;
    const { pageContext } = useComponentContext();

    let initiallyCollapsed = false;
    if (section.query) {
        initiallyCollapsed = tryGetLocalStorageValue(`repos.prquery.${section.query.id}.collapsed`) === "true";
    }

    const [collapsed, setCollapsed] = useObservable(initiallyCollapsed);

    const headerRef = React.createRef<Header>();
    const focusCustomizeButton = () => {
        headerRef.current?.focus({ commandBarItemId: "customize-view" });
    };

    const commandBarItems: IHeaderCommandBarItem[] | undefined =
        section.customizable && onCustomizeUserView
            ? [{ id: "customize-view", onActivate: () => onCustomizeUserView(focusCustomizeButton), text: Resources.Customize }]
            : undefined;

    const tabInfo = section.tabInfo;

    const [columns] = React.useState([
        creatorColumn,
        showRepository ? titleColumn : titleNoRepoColumn,
        reviewersColumn,
        commentsColumn,
        updatesColumn,
        new ColumnMore<IPullRequest>(
            pullRequest => {
                const { currentReviewer } = pullRequest;
                const menuItems = [];

                if (canFlag(pageContext, pullRequest)) {
                    menuItems.push({
                        iconProps: { iconName: "Flag" },
                        id: "flag",
                        onActivate: () => onToggleFlagged(pullRequest),
                        text: currentReviewer && currentReviewer.isFlagged ? Resources.Unflag : Resources.Flag
                    });
                }

                if (canDecline(pageContext, pullRequest)) {
                    menuItems.push({
                        id: "decline",
                        onActivate: () => onToggleDecline(pullRequest),
                        text: currentReviewer && currentReviewer.hasDeclined ? Resources.RejoinTheReview : Resources.DeclineToReview
                    });
                }

                return {
                    id: "pull-requests-more-action-menu",
                    items: menuItems
                };
            },
            pullRequest => section.showFlagAndDeclineMenuItems && (canFlag(pageContext, pullRequest) || canDecline(pageContext, pullRequest))
        )
    ]);

    return (
        <CustomCard className={css("flex-noshrink repos-pr-section-card", (section.title || commandBarItems) && "bolt-card-with-header")}>
            {
                <Observer collapsed={collapsed}>
                    {({ collapsed }: { collapsed: boolean }) => {
                        return (
                            <>
                                {(section.title || commandBarItems) && (
                                    <Observer countText={section.countText} tabs={tabInfo?.tabs}>
                                        {({ countText, tabs }: { countText: string; tabs: IVssContributedTab[] | undefined }) => {
                                            const showTabs = !collapsed && tabInfo && tabInfo.tabs.length > 1;

                                            let title: React.ReactNode = <span className="text-ellipsis">{section.title}</span>;
                                            if (countText && (!showTabs || section.hasMoreResults.value)) {
                                                title = (
                                                    <>
                                                        {title}
                                                        <Pill className="margin-left-8" excludeFocusZone={true} excludeTabStop={true}>
                                                            {countText}
                                                        </Pill>
                                                    </>
                                                );
                                            }
                                            title = <div className="repos-pr-section-header-title flex-row">{title}</div>;

                                            if (showTabs && tabInfo) {
                                                title = (
                                                    <div className="flex-column">
                                                        {title}
                                                        <TabProvider providers={tabInfo.tabs} selectedTabId={tabInfo.selectedTabId}>
                                                            <TabBar
                                                                className="body-m repos-pr-section-tabs"
                                                                disableSticky={true}
                                                                onSelectedTabChanged={newTab => tabInfo.setSelectedTab(newTab)}
                                                            />
                                                        </TabProvider>
                                                    </div>
                                                );
                                            }

                                            return (
                                                <div className="flex-row">
                                                    <Button
                                                        ariaLabel={
                                                            collapsed
                                                                ? format(Resources.ExpandSection, section.title)
                                                                : format(Resources.CollapseSection, section.title)
                                                        }
                                                        subtle={true}
                                                        iconProps={{
                                                            iconName: collapsed ? "ChevronRightMed" : "ChevronDownMed",
                                                            size: IconSize.small
                                                        }}
                                                        className="bolt-card-expand-button flex-self-start repos-pr-section-expand-button"
                                                        onClick={() => {
                                                            setCollapsed(!collapsed);
                                                            if (section.query) {
                                                                trySetLocalStorageValue(
                                                                    `repos.prquery.${section.query.id}.collapsed`,
                                                                    collapsed ? undefined : "true"
                                                                );
                                                            }
                                                        }}
                                                    />
                                                    <ScreenSizeObserver>
                                                        {({ screenSize }: { screenSize: ScreenSize }) => {
                                                            return (
                                                                <Header
                                                                    className={css(
                                                                        "flex-grow bolt-card-header repos-pr-section-header scroll-hidden",
                                                                        showTabs && "with-tabs"
                                                                    )}
                                                                    commandBarItems={screenSize > ScreenSize.xsmall ? commandBarItems : undefined}
                                                                    ref={headerRef}
                                                                    title={title}
                                                                />
                                                            );
                                                        }}
                                                    </ScreenSizeObserver>
                                                </div>
                                            );
                                        }}
                                    </Observer>
                                )}
                                {!collapsed && (
                                    <CardContent className={css(collapsed && "hidden")} contentPadding={false}>
                                        <div className="flex-column scroll-hidden">
                                            <Observer pullRequests={section.pullRequests}>
                                                {() => {
                                                    return section.pullRequests.length === 0 ? (
                                                        tabInfo && tabInfo.tabs.length > 1 ? (
                                                            <div className="repos-pr-section-no-results-message padding-16 secondary-text">
                                                                {Resources.NoCustomPullRequests}
                                                            </div>
                                                        ) : (
                                                            <ZeroData
                                                                className="flex-grow margin-bottom-16"
                                                                imagePath={getAssetLocation(
                                                                    pageContext,
                                                                    "ms.vss-code-web/pr-list/Content/emptyPRList.svg"
                                                                )}
                                                                imageAltText={Resources.EmptyPrListingPrimary}
                                                                primaryText={Resources.NoCustomPullRequests}
                                                            />
                                                        )
                                                    ) : null;
                                                }}
                                            </Observer>
                                            <PullRequestTable
                                                columns={columns}
                                                loadMore={section.loadMore}
                                                onActivate={onActivateRow}
                                                pullRequests={section.pullRequests}
                                                tableBreakpoints={tableBreakpoints}
                                                viewMoreLinkProps={section.loadMoreLinkProps}
                                            />
                                        </div>
                                    </CardContent>
                                )}
                            </>
                        );
                    }}
                </Observer>
            }
        </CustomCard>
    );
};

function canDecline(pageContext: IVssPageContext, pullRequest: IPullRequest): boolean {
    const currentUserId = pageContext.getService<IVssPageService>("IVssPageService").getData()!.user.id;
    const hasVoted = pullRequest.currentReviewer && pullRequest.currentReviewer.vote !== PullRequestVote.awaitingResponse;
    const isRequired = pullRequest.currentReviewer && pullRequest.currentReviewer.required;
    return (
        pullRequest.status === PullRequestStatus.Active &&
        currentUserId !== pullRequest.createdBy.id &&
        !hasVoted &&
        !isRequired &&
        isFeatureFlagEnabled(pageContext, "SourceControl.GitPullRequests.ReviewerFlags", false)
    );
}

function canFlag(pageContext: IVssPageContext, pullRequest: IPullRequest): boolean {
    const currentUserId = pageContext.getService<IVssPageService>("IVssPageService").getData()!.user.id;
    return (
        pullRequest.status === PullRequestStatus.Active &&
        currentUserId !== pullRequest.createdBy.id &&
        isFeatureFlagEnabled(pageContext, "SourceControl.GitPullRequests.ReviewerFlags", false)
    );
}
